import { createSerializationAdapter } from "@tanstack/react-router";

/**
 * Typed user-facing error received from the Python service over the
 * BFF<->Python internal API.
 *
 * Lives in `lib/` (isomorphic) so it can be imported by both client-bundled
 * code (`api/middleware/safe-error.ts`) and server-only code
 * (`server/data/internal-api/client.ts`) without pulling node:* into the
 * client bundle. Do not add node:* imports to this file.
 */
export class BffUserFacingError extends Error {
  readonly tag: string;
  readonly status: number;

  constructor(tag: string, message: string, status: number) {
    super(message);
    this.name = "BffUserFacingError";
    this.tag = tag;
    this.status = status;
  }
}

// Kept as a client-side fallback check for errors that cross the wire
// wrapped or re-thrown outside the serialization adapters below.
export const UNAUTHORIZED_MESSAGE = "Your session has expired. Please sign in again.";

/**
 * Thrown by `requireAuth` when a server function is called without a valid
 * session. The "unauthorized" tag is on the user-facing allowlist
 * (api/middleware/safe-error.ts), so the client receives this message intact
 * and can distinguish an expired session from a scrubbed internal error.
 */
export class UnauthorizedError extends Error {
  readonly tag = "unauthorized";

  constructor(message = UNAUTHORIZED_MESSAGE) {
    super(message);
    this.name = "UnauthorizedError";
  }
}

/**
 * Whether a thrown value means "this session is gone" — the one question the
 * root global-state interception and the error-component backstop both ask, so
 * neither can answer it differently.
 *
 * Three shapes, all the same fact. The BFF throws `UnauthorizedError`; Python's
 * session expiry arrives as a `BffUserFacingError` tagged "unauthorized" (whose
 * message is just the tag, hence `UNAUTHORIZED_MESSAGE` above for display); and
 * the message compare catches an error re-thrown or wrapped outside the
 * serialization adapters, which keep only name and message.
 */
export function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof UnauthorizedError) return true;
  if (error instanceof BffUserFacingError) return error.tag === "unauthorized";
  return error instanceof Error && error.message === UNAUTHORIZED_MESSAGE;
}

/**
 * What to show a user about a failure they caused or witnessed.
 *
 * Server-function errors are scrubbed on the way out unless their tag is on the
 * user-facing allowlist, so this is either a real explanation (a
 * `duplicate-name:*` refusal, `role-forbidden`) or a generic one — either way it
 * is the most the browser has been told, and printing it beats an unexplained
 * empty panel. The tag itself is deliberately not shown: the message Python
 * wrote already reads for the person who typed the name.
 *
 * Isomorphic and trivial, and here rather than private to a panel because the
 * admin surfaces all make this same noise and a per-panel copy is how two
 * screens end up disagreeing about how a failure reads.
 */
export const errorMessageForDisplay = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

// Start's default error serialization (seroval) keeps only name/message, so a
// custom class arrives client-side as a plain Error with the tag gone. These
// adapters (registered in start.ts `serializationAdapters`) round-trip our
// user-facing error classes across SSR streaming and server-fn RPC, so the
// client can branch on `instanceof` / `.tag` instead of message text.
export const unauthorizedErrorSerializationAdapter = createSerializationAdapter({
  key: "unauthorized-error",
  test: (v): v is UnauthorizedError => v instanceof UnauthorizedError,
  toSerializable: (e) => ({ message: e.message }),
  fromSerializable: (s) => new UnauthorizedError(s.message),
});

export const bffUserFacingErrorSerializationAdapter = createSerializationAdapter({
  key: "bff-user-facing-error",
  test: (v): v is BffUserFacingError => v instanceof BffUserFacingError,
  toSerializable: (e) => ({ tag: e.tag, message: e.message, status: e.status }),
  fromSerializable: (s) => new BffUserFacingError(s.tag, s.message, s.status),
});
